// Panneau Opérateurs — voir les opérateurs connectés à la session et les
// déconnecter. Les pairs sont reçus en continu via `session:state.peers` ;
// la déconnexion envoie `peer:disconnect` au relay (handler peer.js).

import { createSignal, For, Show, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { registerPanel } from "@/panel-registry";
import { useToolClient } from "@/shared/tool-client";
import { useWs, type SessionPeer } from "@/shared/ws";
import "./operators.css";

const ROLE_LABELS: Record<string, string> = {
  operator: "Opérateur",
  "operator-media": "Opérateur (média)",
  agent: "Agent",
  client: "Client",
};

const OperatorsPanel: Component = () => {
  const ws = useWs();
  const tools = useToolClient();
  const [maxOperators, setMaxOperators] = createSignal(
    Math.max(1, Number(localStorage.getItem("rws.maxOperators") || "3") || 3),
  );
  const [busy, setBusy] = createSignal(false);
  const [notice, setNotice] = createSignal<string | null>(null);
  const peers = (): SessionPeer[] => ws.state.peers || [];
  const operators = () => peers().filter((p) => p.role === "operator" || p.role === "operator-media");
  const me = () => operators().find((p) => p.id === ws.state.myPeerId) || null;
  const otherOperators = () => operators().filter((p) => p.id !== ws.state.myPeerId);
  const others = () => peers().filter((p) => p.role !== "operator" && p.role !== "operator-media");
  const overLimit = () => operators().length > maxOperators();

  async function cleanupLiveWatchers(): Promise<void> {
    try {
      await tools.call("process_watch_stop", {}, { timeoutMs: 6_000 });
      setNotice("Watchers Process nettoyés avant déconnexion.");
    } catch {
      setNotice("Déconnexion lancée, nettoyage Process non confirmé.");
    }
  }

  async function disconnect(p: SessionPeer): Promise<void> {
    if (p.id === ws.state.myPeerId) return;
    setBusy(true);
    // Ne PAS nettoyer les watchers Process ici : `process_watch_stop` sans
    // jobId couperait le watcher de TOUS les opérateurs. Déconnecter un autre
    // pair ne doit pas toucher nos abonnements — l'agent purge seul via le TTL
    // d'abonnés du pair déconnecté.
    ws.send({ type: "peer:disconnect", peerId: p.id });
    setBusy(false);
  }

  async function closeMySession(): Promise<void> {
    const ok = window.confirm(
      "Fermer uniquement votre session opérateur ? Changer de panneau ou de page interne ne ferme pas la session.",
    );
    if (!ok) return;
    setBusy(true);
    await cleanupLiveWatchers();
    const id = ws.state.myPeerId;
    if (id) ws.send({ type: "peer:disconnect", peerId: id });
    setTimeout(() => {
      window.location.assign("/console/");
    }, 120);
  }

  async function disconnectOthers(): Promise<void> {
    const targets = otherOperators();
    if (!targets.length) return;
    setBusy(true);
    // Pas de cleanupLiveWatchers() : on ne ferme pas NOTRE session, et le
    // `process_watch_stop` global couperait notre propre watcher. L'agent
    // purge les abonnements des pairs déconnectés via leur TTL.
    for (const p of targets) {
      ws.send({ type: "peer:disconnect", peerId: p.id });
    }
    setBusy(false);
  }

  async function enforceLimit(): Promise<void> {
    const max = maxOperators();
    const extras = otherOperators()
      .slice()
      .sort((a, b) => (a.connectedAt || 0) - (b.connectedAt || 0))
      .slice(0, Math.max(0, operators().length - max));
    if (!extras.length) return;
    setBusy(true);
    // Idem : ne pas couper le watcher global. Seuls les pairs en excès sont
    // déconnectés, leurs abonnements expirent côté agent via le TTL.
    for (const p of extras) {
      ws.send({ type: "peer:disconnect", peerId: p.id });
    }
    setBusy(false);
  }

  function updateMax(value: string): void {
    const n = Math.max(1, Math.min(20, Number(value) || 1));
    setMaxOperators(n);
    localStorage.setItem("rws.maxOperators", String(n));
  }

  function fmtTime(ts?: number): string {
    if (!ts) return "";
    try {
      return new Date(ts).toLocaleTimeString();
    } catch {
      return "";
    }
  }

  return (
    <PanelLayout
      title="Opérateurs"
      status={{
        text: `${operators().length}/${maxOperators()} opérateur(s)`,
        level: overLimit() ? "warn" : "info",
      }}
    >
      <div class="operators-panel">
        <div class="operators-toolbar">
          <div>
            <div class="operators-toolbar__title">Session active</div>
            <div class="operators-toolbar__meta">
              Changer de panneau garde la connexion. Utiliser “Fermer ma session” pour quitter.
            </div>
          </div>
          <div class="operators-toolbar__actions">
            <label class="operators-limit">
              <span>Limite</span>
              <input
                type="number"
                min="1"
                max="20"
                value={maxOperators()}
                onInput={(e) => updateMax(e.currentTarget.value)}
              />
            </label>
            <button
              type="button"
              class="btn btn--xs btn--ghost"
              disabled={!otherOperators().length || busy()}
              onClick={enforceLimit}
            >
              Ramener à {maxOperators()}
            </button>
            <button
              type="button"
              class="btn btn--xs btn--danger"
              disabled={!otherOperators().length || busy()}
              onClick={disconnectOthers}
            >
              Éjecter les autres
            </button>
            <button type="button" class="btn btn--xs btn--primary" disabled={!me() || busy()} onClick={closeMySession}>
              {busy() ? "Fermeture…" : "Fermer ma session"}
            </button>
          </div>
        </div>

        <Show when={notice()}>
          <div class="operators-warning">{notice()}</div>
        </Show>

        <Show when={overLimit()}>
          <div class="operators-warning">
            {operators().length} opérateurs connectés pour une limite souhaitée de {maxOperators()}.
          </div>
        </Show>

        <div class="operators-window" role="dialog" aria-label="Opérateurs actifs">
          <div class="operators-window__header">
            <div>
              <div class="operators-window__title">Opérateurs actifs</div>
              <div class="operators-window__subtitle">
                Déconnexion explicite uniquement, jamais au changement de panneau.
              </div>
            </div>
            <span class="operators-window__count">{operators().length}</span>
          </div>
        </div>

        <div class="operators-group">
          <div class="operators-group__title">Opérateurs connectés ({operators().length})</div>
          <Show
            when={operators().length}
            fallback={<div class="panel-data-empty">Aucun opérateur dans la session.</div>}
          >
            <For each={operators()}>
              {(p) => (
                <div class="peer-row">
                  <div class="peer-row__main">
                    <span class="peer-row__name">{p.label || p.id}</span>
                    <Show when={p.id === ws.state.myPeerId}>
                      <span class="badge badge--info">vous</span>
                    </Show>
                    <Show when={p.active}>
                      <span class="badge badge--ok">actif</span>
                    </Show>
                    <span class="badge badge--muted">{ROLE_LABELS[p.role] || p.role}</span>
                  </div>
                  <div class="peer-row__meta">
                    {p.platform || "—"}
                    <Show when={fmtTime(p.connectedAt)}> · connecté {fmtTime(p.connectedAt)}</Show>
                  </div>
                  <Show
                    when={p.id !== ws.state.myPeerId}
                    fallback={
                      <button type="button" class="btn btn--xs btn--ghost" disabled>
                        Votre session
                      </button>
                    }
                  >
                    <button
                      type="button"
                      class="btn btn--xs btn--danger"
                      disabled={busy()}
                      onClick={() => disconnect(p)}
                    >
                      Déconnecter
                    </button>
                  </Show>
                </div>
              )}
            </For>
          </Show>
        </div>

        <Show when={others().length}>
          <div class="operators-group">
            <div class="operators-group__title">Autres pairs ({others().length})</div>
            <For each={others()}>
              {(p) => (
                <div class="peer-row peer-row--info">
                  <div class="peer-row__main">
                    <span class="peer-row__name">{p.label || p.id}</span>
                    <span class="badge badge--muted">{ROLE_LABELS[p.role] || p.role}</span>
                  </div>
                  <div class="peer-row__meta">{p.platform || "—"}</div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
    </PanelLayout>
  );
};

registerPanel("operators", OperatorsPanel);
